import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, KeyRound, Loader2, ChevronRight } from 'lucide-react';
import { Card, CardHeader, CardTitle } from './ui/Card';
import mfaService, { type EnrolledMethods } from '../services/mfa';

/**
 * Dashboard account snapshot card — shows 2FA status at a glance.
 * Click anywhere to manage in /settings/security (which also shows profile info).
 */
export function AccountCard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [mfa, setMfa] = useState<EnrolledMethods | null>(null);

  useEffect(() => {
    let cancelled = false;
    mfaService.getMethods()
      .then((m) => { if (!cancelled) setMfa(m); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const hasAnyMfa = mfa
    ? mfa.totp || mfa.email || mfa.passkeys.length > 0
    : false;

  return (
    <Card
      className="hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
      onClick={() => navigate('/settings/security')}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-lg text-neutral-600 flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary-600" />
          Account & Security
        </CardTitle>
        <ChevronRight className="h-4 w-4 text-neutral-400" />
      </CardHeader>

      <div className="px-6 pb-4">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-neutral-400" />
          <span className="text-sm text-neutral-600">Two-factor auth</span>
          {loading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-neutral-300 ml-auto" />
          ) : hasAnyMfa ? (
            <span className="ml-auto text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
              Enabled
            </span>
          ) : (
            <span className="ml-auto text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
              Off
            </span>
          )}
        </div>
        <p className="text-xs text-neutral-400 mt-1.5">Tap to manage profile &amp; security</p>
      </div>
    </Card>
  );
}

export default AccountCard;
